/* =========================================================
   NESIUM ESSENTIAL — app.js (no dependencies)
   ========================================================= */
(function () {
  'use strict';
  const N = window.Nesium || { routes: {}, strings: {}, moneyFormat: '${{amount}}' };

  /* ---------- helpers ---------- */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const format = N.moneyFormat || '${{amount}}';
    function delimit(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount': value = delimit(cents, 2); break;
      case 'amount_no_decimals': value = delimit(cents, 0); break;
      case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
      default: value = delimit(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  async function fetchJSON(url, opts) {
    const res = await fetch(url, opts);
    if (!res.ok) throw await res.json().catch(() => ({ status: res.status }));
    return res.json();
  }

  /* ---------- Cart ---------- */
  const Cart = {
    async add(items) {
      return fetchJSON(N.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
    },
    async change(line, quantity) {
      return fetchJSON(N.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
    },
    async refresh(open) {
      try {
        const [sectionRes, cart] = await Promise.all([
          fetch(N.routes.root + '?sections=cart-drawer').then((r) => r.json()),
          fetchJSON(N.routes.cart + '.js')
        ]);
        const html = sectionRes['cart-drawer'];
        const holder = $('#CartDrawer');
        if (holder && html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = $('#CartDrawer', doc);
          if (fresh) holder.innerHTML = fresh.innerHTML;
        }
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.classList.toggle('is-empty', cart.item_count === 0);
        });
        if (open) Drawer.open('#CartDrawer');
      } catch (e) { /* silent */ }
    },
    afterAdd() {
      if (N.cartType === 'page') { window.location.href = N.routes.cart; return; }
      Cart.refresh(true);
    }
  };

  /* ---------- Drawers ---------- */
  const Drawer = {
    open(sel) {
      const d = $(sel);
      if (!d) return;
      d.classList.add('is-open');
      d.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';
      const focusable = $('[data-drawer-close]', d) || d;
      requestAnimationFrame(() => focusable.focus && focusable.focus());
      Drawer._active = d;
    },
    close(d) {
      d = d || Drawer._active;
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      Drawer._active = null;
    }
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); Drawer.open(opener.getAttribute('data-drawer-open')); return; }
    if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('drawer__overlay')) {
      const d = e.target.closest('.drawer');
      Drawer.close(d);
    }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') Drawer.close(); });

  /* ---------- Cart line events (delegated — survives drawer re-render) ---------- */
  function afterLineChange() {
    if ($('[data-cart-page]')) { window.location.reload(); return; }
    Cart.refresh(false);
  }
  document.addEventListener('change', (e) => {
    const qty = e.target.closest('[data-cart-qty]');
    if (!qty) return;
    const line = parseInt(qty.getAttribute('data-line'), 10);
    const val = Math.max(0, parseInt(qty.value, 10) || 0);
    Cart.change(line, val).then(afterLineChange).catch(() => alert(N.strings.updateError));
  });
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-cart-remove]');
    if (!rm) return;
    e.preventDefault();
    const line = parseInt(rm.getAttribute('data-line'), 10);
    Cart.change(line, 0).then(afterLineChange).catch(() => alert(N.strings.updateError));
  });

  /* ---------- Quick add (product cards) ---------- */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-quick-add]');
    if (!form) return;
    e.preventDefault();
    const btn = $('button[type="submit"]', form);
    const id = parseInt($('[name="id"]', form).value, 10);
    const original = btn.textContent;
    btn.textContent = N.strings.adding;
    btn.disabled = true;
    Cart.add([{ id, quantity: 1 }])
      .then(() => { btn.textContent = N.strings.added; Cart.afterAdd(); })
      .catch(() => { alert(N.strings.addError); })
      .finally(() => setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1600));
  });

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        e.preventDefault();
        const step = btn.hasAttribute('data-minus') ? -1 : 1;
        const min = parseInt(this.input.min || '1', 10);
        const max = parseInt(this.input.max || '99', 10);
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
        if (next !== parseInt(this.input.value, 10)) {
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Product form (variants + selling plans) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      const dataEl = $('script[data-product-json]', this);
      if (!dataEl) return;
      this.data = JSON.parse(dataEl.textContent);
      this.form = $('form[data-product-form]', this);
      this.idInput = $('[name="id"]', this.form);
      this.planInput = $('[name="selling_plan"]', this.form);
      this.submitBtn = $('[data-atc]', this);
      this.freqSelect = $('[data-plan-select]', this);
      this.freqWrap = $('[data-plan-wrap]', this);

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-opt]')) this.onOptionChange();
        if (e.target.matches('[data-purchase]')) this.onPurchaseChange();
        if (e.target.matches('[data-plan-select]')) this.render();
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.render();
    }
    get variant() {
      const id = parseInt(this.idInput.value, 10);
      return this.data.variants.find((v) => v.id === id) || this.data.variants[0];
    }
    get purchase() {
      const checked = $('[data-purchase]:checked', this);
      return checked ? checked.value : 'onetime';
    }
    currentPlan(variant) {
      if (this.purchase !== 'subscribe' || !variant.plans.length) return null;
      const wanted = this.freqSelect ? this.freqSelect.value : null;
      return variant.plans.find((p) => String(p.plan_id) === String(wanted)) || variant.plans[0];
    }
    onOptionChange() {
      const selected = $$('[data-opt]:checked', this).map((i) => i.value);
      const match = this.data.variants.find((v) =>
        v.options.every((opt, i) => opt === selected[i])
      );
      if (match) {
        this.idInput.value = match.id;
        const url = new URL(window.location);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }
      this.render();
    }
    onPurchaseChange() { this.render(); }
    render() {
      const v = this.variant;
      const plan = this.currentPlan(v);
      const price = plan ? plan.price : v.price;
      const compare = plan ? v.price : (v.compare_at_price > v.price ? v.compare_at_price : 0);

      if (this.freqWrap) this.freqWrap.hidden = this.purchase !== 'subscribe' || !v.plans.length;
      if (this.planInput) this.planInput.value = plan ? plan.plan_id : '';

      $$('[data-price]', this).forEach((el) => (el.textContent = formatMoney(price)));
      $$('[data-compare]', this).forEach((el) => {
        el.textContent = compare ? formatMoney(compare) : '';
        el.hidden = !compare;
      });
      $$('[data-save-badge]', this).forEach((el) => {
        if (compare && compare > price) {
          el.hidden = false;
          $('[data-save-pct]', el) && ($('[data-save-pct]', el).textContent = Math.round(((compare - price) / compare) * 100));
        } else el.hidden = true;
      });
      // subscribe option label price
      const subPriceEl = $('[data-sub-price]', this);
      if (subPriceEl && v.plans.length) subPriceEl.textContent = formatMoney(v.plans[0].price);
      const oncePriceEl = $('[data-once-price]', this);
      if (oncePriceEl) oncePriceEl.textContent = formatMoney(v.price);

      if (this.submitBtn) {
        if (!v.available) {
          this.submitBtn.disabled = true;
          this.submitBtn.textContent = N.strings.soldOut;
        } else {
          this.submitBtn.disabled = false;
          $('[data-atc-label]', this.submitBtn).textContent = N.strings.addToCart;
          $('[data-atc-price]', this.submitBtn) && ($('[data-atc-price]', this.submitBtn).textContent = formatMoney(price));
        }
      }
      // sync sticky bar
      const sticky = $('[data-sticky-bar]');
      if (sticky) {
        const p = $('[data-sticky-price]', sticky);
        if (p) p.textContent = formatMoney(price);
      }
      // swap gallery image if the variant has one
      if (v.image && this.dataset.section) {
        const mainImg = $(`[data-gallery="${this.dataset.section}"] [data-gallery-main] img`);
        if (mainImg && mainImg.src !== v.image) {
          mainImg.src = v.image;
          if (mainImg.srcset) mainImg.removeAttribute('srcset');
        }
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: v } }));
    }
    async onSubmit(e) {
      e.preventDefault();
      const v = this.variant;
      if (!v.available) return;
      const qty = parseInt(($('[name="quantity"]', this.form) || {}).value || '1', 10);
      const item = { id: v.id, quantity: qty };
      if (this.planInput && this.planInput.value) item.selling_plan = parseInt(this.planInput.value, 10);
      const label = $('[data-atc-label]', this.submitBtn);
      const original = label.textContent;
      label.textContent = N.strings.adding;
      this.submitBtn.disabled = true;
      try {
        await Cart.add([item]);
        label.textContent = N.strings.added;
        Cart.afterAdd();
      } catch (err) {
        alert((err && err.description) || N.strings.addError);
      } finally {
        setTimeout(() => { label.textContent = original; this.submitBtn.disabled = false; }, 1600);
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Bundle builder ---------- */
  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.tier2 = parseFloat(this.dataset.tier2 || '0');
      this.tier3 = parseFloat(this.dataset.tier3 || '0');
      this.tiersOn = this.dataset.tiersEnabled === 'true';
      this.items = $$('[data-bundle-item]', this).map((el) => {
        const json = $('script[data-bundle-product]', el);
        return { el, data: json ? JSON.parse(json.textContent) : null };
      }).filter((i) => i.data);

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-include], [data-bopt], [data-bvariant], [data-btype], [data-bfreq]')) this.render();
        if (e.target.matches('[data-bqty]')) this.render();
      });
      const cta = $('[data-bundle-add]', this);
      if (cta) cta.addEventListener('click', (e) => this.submit(e, cta));
      const stickyBtn = $('[data-bsticky-add]', this);
      if (stickyBtn && cta) stickyBtn.addEventListener('click', () => cta.click());
      this.initSticky();
      this.render();
    }
    initSticky() {
      const bar = $('[data-bundle-sticky]', this);
      const card = $('.script-card', this);
      if (!bar || !card || !('IntersectionObserver' in window)) return;
      bar.hidden = false;
      let builderIn = false, cardIn = false;
      const sync = () => bar.classList.toggle('is-visible', builderIn && !cardIn && this._count > 0);
      new IntersectionObserver((entries) => { builderIn = entries[0].isIntersecting; sync(); }, { rootMargin: '-15% 0px -15% 0px' }).observe(this);
      new IntersectionObserver((entries) => { cardIn = entries[0].isIntersecting; sync(); }, { threshold: 0.1 }).observe(card);
      this._syncSticky = sync;
    }
    get mode() {
      const t = $('[data-btype]:checked', this);
      return t ? t.value : 'subscribe';
    }
    get freqLabel() {
      const f = $('[data-bfreq]', this);
      return f && !f.closest('[hidden]') ? f.options[f.selectedIndex].text.trim() : null;
    }
    stateFor(item) {
      const included = $('[data-include]', item.el).checked;
      let variant = null;
      const opts = $$('[data-bopt]:checked', item.el);
      if (opts.length) {
        const selected = opts
          .sort((a, b) => parseInt(a.dataset.optPos, 10) - parseInt(b.dataset.optPos, 10))
          .map((i) => i.value);
        variant = item.data.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      } else {
        const vInput = $('[data-bvariant]:checked', item.el);
        if (vInput) variant = item.data.variants.find((v) => String(v.id) === String(vInput.value));
      }
      variant = variant || item.data.variants[0];
      const qty = parseInt(($('[data-bqty]', item.el) || {}).value || '1', 10);
      let plan = null;
      if (this.mode === 'subscribe' && variant.plans.length) {
        const label = this.freqLabel;
        plan = (label && variant.plans.find((p) => p.name.trim() === label)) || variant.plans[0];
      }
      return { included, variant, qty, plan };
    }
    render() {
      const freqWrap = $('[data-bfreq-wrap]', this);
      const anyPlans = this.items.some((i) => i.data.variants.some((v) => v.plans.length));
      if (freqWrap) freqWrap.hidden = this.mode !== 'subscribe' || !anyPlans;

      let count = 0, subtotal = 0, compareTotal = 0;
      const lines = [];
      this.items.forEach((item) => {
        const s = this.stateFor(item);
        const unit = s.plan ? s.plan.price : s.variant.price;
        const priceEl = $('[data-bprice]', item.el);
        const compEl = $('[data-bcompare]', item.el);
        item.el.classList.toggle('is-unavailable', !s.variant.available);
        if (priceEl) {
          priceEl.textContent = s.variant.available ? formatMoney(unit) : N.strings.soldOut;
          priceEl.classList.toggle('bundle-item__soldout', !s.variant.available);
        }
        if (compEl) {
          const showComp = s.variant.available && s.plan && s.plan.price < s.variant.price;
          compEl.hidden = !showComp;
          if (showComp) compEl.textContent = formatMoney(s.variant.price);
        }
        item.el.classList.toggle('is-included', s.included);
        if (!s.included || !s.variant.available) return;
        count += 1;
        subtotal += unit * s.qty;
        compareTotal += s.variant.price * s.qty;
        lines.push({ title: item.data.title, dose: s.variant.title, qty: s.qty, total: unit * s.qty });
      });

      const tierPct = this.tiersOn && this.mode === 'onetime' ? (count >= 3 ? this.tier3 : count === 2 ? this.tier2 : 0) : 0;
      const tierAmount = Math.round(subtotal * (tierPct / 100));
      const subSavings = compareTotal - subtotal;
      const total = subtotal - tierAmount;

      // summary list
      const list = $('[data-sum-list]', this);
      if (list) {
        list.innerHTML = lines.map((l) =>
          `<li class="script-line"><span>${l.qty} × ${escapeHtml(l.title)} <span class="mono muted">${escapeHtml(l.dose)}</span></span><span class="mono">${formatMoney(l.total)}</span></li>`
        ).join('') || `<li class="script-line script-line--empty muted">${N.strings.selectMin}</li>`;
      }
      setText(this, '[data-sum-count]', count);
      const compRow = $('[data-sum-compare-row]', this);
      if (compRow) {
        compRow.hidden = !(this.mode === 'subscribe' && subSavings > 0);
        setText(this, '[data-sum-compare]', formatMoney(compareTotal));
        setText(this, '[data-sum-sub-savings]', '−' + formatMoney(subSavings));
      }
      const tierRow = $('[data-sum-tier-row]', this);
      if (tierRow) {
        tierRow.hidden = !(tierPct > 0);
        setText(this, '[data-sum-tier-pct]', tierPct);
        setText(this, '[data-sum-tier]', '−' + formatMoney(tierAmount));
      }
      setText(this, '[data-sum-total]', formatMoney(total));
      const per = $('[data-sum-per]', this);
      if (per) per.hidden = this.mode !== 'subscribe';

      const cta = $('[data-bundle-add]', this);
      if (cta) {
        cta.disabled = count === 0;
        setText(this, '[data-cta-price]', count ? '— ' + formatMoney(total) : '');
      }
      this._count = count;
      setText(this, '[data-bsticky-count]', count);
      setText(this, '[data-bsticky-total]', count ? formatMoney(total) : '—');
      const stickyBtn = $('[data-bsticky-add]', this);
      if (stickyBtn) stickyBtn.disabled = count === 0;
      if (this._syncSticky) this._syncSticky();
      this._pending = { count };
    }
    async submit(e, cta) {
      e.preventDefault();
      const items = [];
      this.items.forEach((item) => {
        const s = this.stateFor(item);
        if (!s.included || !s.variant.available) return;
        const line = { id: s.variant.id, quantity: s.qty, properties: { _bundle: 'Nesium Stack' } };
        if (s.plan) line.selling_plan = s.plan.plan_id;
        items.push(line);
      });
      if (!items.length) { this.flashError(N.strings.selectMin); return; }
      const label = $('[data-cta-label]', cta);
      const original = label.textContent;
      label.textContent = N.strings.adding;
      cta.disabled = true;
      try {
        await Cart.add(items);
        label.textContent = N.strings.stackAdded;
        Cart.afterAdd();
      } catch (err) {
        this.flashError((err && err.description) || N.strings.addError);
      } finally {
        setTimeout(() => { label.textContent = original; cta.disabled = false; }, 1800);
      }
    }
    flashError(msg) {
      const box = $('[data-bundle-error]', this);
      if (!box) return alert(msg);
      box.textContent = msg;
      box.hidden = false;
      setTimeout(() => (box.hidden = true), 3500);
    }
  }
  customElements.define('bundle-builder', BundleBuilder);

  function setText(root, sel, val) { const el = $(sel, root); if (el) el.textContent = val; }
  function escapeHtml(str) { const d = document.createElement('div'); d.textContent = str; return d.innerHTML; }

  /* ---------- Header ---------- */
  const header = $('.site-header');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Sliders ---------- */
  $$('[data-slider]').forEach((slider) => {
    const track = $('.slider__track', slider);
    if (!track) return;
    slider.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-slide]');
      if (!btn) return;
      const dir = btn.getAttribute('data-slide') === 'next' ? 1 : -1;
      const item = track.firstElementChild;
      const w = item ? item.getBoundingClientRect().width + 18 : 320;
      track.scrollBy({ left: dir * w, behavior: 'smooth' });
    });
  });

  /* ---------- Reveal on scroll ---------- */
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    $$('.reveal').forEach((el) => io.observe(el));
  } else {
    $$('.reveal').forEach((el) => el.classList.add('is-in'));
  }

  /* ---------- Sticky ATC bar ---------- */
  const stickyBar = $('[data-sticky-bar]');
  const stickyAnchor = $('[data-sticky-anchor]');
  if (stickyBar && stickyAnchor && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      stickyBar.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
    }, { threshold: 0 });
    io.observe(stickyAnchor);
    const proxy = $('[data-sticky-submit]', stickyBar);
    if (proxy) proxy.addEventListener('click', () => {
      const mainBtn = $('product-form [data-atc]');
      if (mainBtn) mainBtn.click();
    });
  }

  /* ---------- Product gallery thumbs ---------- */
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-thumb]');
    if (!thumb) return;
    const gallery = thumb.closest('[data-gallery]');
    const main = $('[data-gallery-main] img', gallery);
    const img = $('img', thumb);
    if (main && img) {
      main.src = img.getAttribute('data-full') || img.src;
      main.srcset = '';
      $$('[data-thumb]', gallery).forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    }
  });
})();
